import { useQuery } from '@apollo/client';
import { Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { X as Close } from '@styled-icons/bootstrap/X';
import { ReactComponent as DismissDocument } from 'assets/img/icons/dismiss-document.svg';
import Loader from 'Components/Forms/Loader';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import { removeRequestFromCachePartnerHome } from 'FeatureModules/Partner/utils/partner.utils';
import {
  SlidesCloseButton,
  SlidesContainer,
  SlidesDialog,
} from 'FeatureModules/ImpactClaim/Components/styles';
import ErrorState from 'Components/ErrorState';
import { DialogTitle, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import DocumentUpload from 'FeatureModules/Document/Components/DocumentUpload';
import { UploadProvider } from 'FeatureModules/Document/Contexts/UploadContext';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import ReceivedRequestCard from 'FeatureModules/Home/Components/Requests/ReceivedRequestCard';
import { GET_PARTNER_REQUEST } from 'graphql/secure/queries';
import { IGetPartnerRequestData } from 'graphql/secure/queries/partner.queries';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import RequestTitle from '../RequestTitle';

interface IUploadDocumentToRequestDialogProps extends IDefaultDialogProps {
  requestId: string;
}

const Container = styled('div')(() => ({
  display: 'flex',
  height: '100%',
  width: '100%',
}));

const UploadColumn = styled('div')(({ theme }) => ({
  width: '50%',
  height: '100%',
  overflow: 'auto',
}));

const RequestColumn = styled('div')(({ theme }) => ({
  width: '50%',
  height: '100%',
  overflow: 'auto',
  padding: theme.spacing(2, 5, 3),
  background: theme.custom.colors.backgroundMedium,
}));

const UploadContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(6, 4),
  background: '#fff',
  padding: theme.spacing(2),
  borderRadius: 6,
  boxShadow: theme.custom.shadows[5],
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gridGap: theme.spacing(1),
  background: '#fff',
  marginTop: 'auto',
  padding: theme.spacing(3),
  position: 'absolute',
  bottom: 0,
  width: '50%',
  boxShadow: theme.shadows[28 as number],
  zIndex: 1,
}));

const DismissIcon = styled(DismissDocument)(() => ({
  width: 10,
}));

const UploadDocumentToRequestDialog: FC<IUploadDocumentToRequestDialogProps> = ({
  open,
  onClose,
  requestId,
}) => {
  const { updatePartnerRequest, loading } = usePartnerRequestActions();
  const { openDialog } = useDialog();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { data, loading: loadingRequest } = useQuery<IGetPartnerRequestData>(GET_PARTNER_REQUEST, {
    variables: {
      id: requestId,
    },
  });
  const { cleanFiles, handleCleanCreatedRecords } = useUploadState();
  const [documents, setDocuments] = useState<IBaseDocument[]>([]);
  const [deletedDocuments, setDeletedDocuments] = useState<string[]>([]);
  const handleDelete = (recordId: string) => {
    setDeletedDocuments([...deletedDocuments, recordId]);
  };
  const disableSubmitButton = documents.length === 0 || !documents[0]?.category;

  useEffect(() => {
    return () => {
      cleanFiles();
      handleCleanCreatedRecords();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadRequest = () => {
    updatePartnerRequest({
      variables: {
        id: requestId,
        input: {
          requestStatus: 'PROVIDED',
          documentId: documents[0].id,
        },
      },

      onError: () =>
        setErrorMessage('Something went wrong uploading the request. Please try again later.'),
      onCompleted: () => {
        setSuccessMessage('You have uploaded the request');
        onClose?.();
      },
      update: (cache, { data }) => {
        if (data) {
          removeRequestFromCachePartnerHome(cache, data.updatePartnerRequest.partnerRequest);
        }
      },
    });
  };

  return (
    <UploadProvider>
      <SlidesDialog
        fullWidth={true}
        maxWidth="lg"
        open={!!open}
        onClose={onClose}
        data-testid="upload-request-dialog"
      >
        <SlidesCloseButton onClick={onClose} data-testid="close-dialog-button">
          <Close size={20} />
        </SlidesCloseButton>
        <SlidesContainer data-testid="upload-dialog">
          {loadingRequest ? (
            <Loader />
          ) : (
            <Container>
              {!data ? (
                <ErrorState error="Cannot load the request" />
              ) : (
                <Fragment>
                  <RequestColumn>
                    <Box mb={4} ml={-3}>
                      <DialogTitle icon={<Icon name="upload" size="large" />}>
                        Upload document
                      </DialogTitle>
                    </Box>
                    <Box mb={3}>
                      <RequestTitle request={data?.partnerRequest as IPartnerRequest} />
                    </Box>
                    <Box mb={3}>
                      <ReceivedRequestCard
                        request={data?.partnerRequest as IPartnerRequest}
                        displayActions={false}
                      />
                    </Box>
                  </RequestColumn>
                  <UploadColumn>
                    <UploadContainer>
                      <DocumentUpload
                        title="Add documents"
                        handleDocumentsChange={setDocuments}
                        documents={documents}
                        deletedDocuments={deletedDocuments}
                        handleDelete={handleDelete}
                        displaySelector
                        data-testid="request-document-upload"
                        multipleDocuments={false}
                        isRequestDocument
                      />
                    </UploadContainer>
                    <ButtonContainer>
                      <ThemeButton
                        color="BLUE_ICE"
                        startIcon={<DismissIcon />}
                        onClick={() => {
                          openDialog({
                            type: 'DENY_REQUEST',
                            props: { requestId },
                          });
                          onClose?.();
                        }}
                      >
                        Dismiss request
                      </ThemeButton>
                      <Box display="flex" gap={1}>
                        <ThemeButton color="BLUE_ICE" size="medium" onClick={onClose}>
                          Cancel
                        </ThemeButton>
                        <Tooltip title="Make sure that all required fields of the document are set">
                          <div>
                            <ThemeButton
                              data-testid="submit-request-form-btn"
                              color="PRIMARY"
                              type="submit"
                              size="medium"
                              onClick={handleUploadRequest}
                              loading={loading}
                              disabled={disableSubmitButton}
                            >
                              Save & Share
                            </ThemeButton>
                          </div>
                        </Tooltip>
                      </Box>
                    </ButtonContainer>
                  </UploadColumn>
                </Fragment>
              )}
            </Container>
          )}
        </SlidesContainer>
      </SlidesDialog>
    </UploadProvider>
  );
};

export default UploadDocumentToRequestDialog;
